import { EventSourceInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { Css, Palette } from "@homebound/beam";
import { addBusinessDays, endOfDay, isEqual, isWithinInterval, startOfDay } from "date-fns";
import { createRef, useEffect, useMemo } from "react";
import { TaskDetailsPage_PlanTaskFragment } from "src/fragments/circularFragments.generated";
import { DynamicSchedulesTaskSnapshot_TaskSnapshotFragment } from "src/routes/projects/dynamic-schedules/components/useScheduleSnapshots.generated";
import { DateOnly } from "src/utils/dates";
import { TaskColorLegend } from "../../schedule-v2/components/TaskColorLegend";
import { DateIcons } from "../calendar/dynamicSchedulesCalendarUtils";
import { getBusinessDaysOptions } from "../utils";
import "./CalendarCard.css";

type CalendarCardProps = {
  task: TaskDetailsPage_PlanTaskFragment;
  taskSnapshot: DynamicSchedulesTaskSnapshot_TaskSnapshotFragment | undefined;
};

export function CalendarCard(props: CalendarCardProps) {
  const { task, taskSnapshot } = props;
  const { workDays, customWorkableDays, schedule } = task;
  const calendarRef = createRef<FullCalendar>();

  // Iterate over the startDate+durationInDays and create an event for each day
  const taskWorkInterval = useMemo(() => {
    const businessDaysOptions = getBusinessDaysOptions(customWorkableDays, schedule.schedulingExclusionDates);

    const taskWorkInterval: Date[] = [];
    if (task.startDate && task.durationInDays) {
      for (let i = 0; i < task.durationInDays; i++) {
        const date = new DateOnly(addBusinessDays(task.startDate, i, businessDaysOptions));
        taskWorkInterval.push(date);
      }
    }
    return taskWorkInterval;
  }, [task.startDate, task.durationInDays, customWorkableDays, schedule.schedulingExclusionDates]);

  const events: EventSourceInput = useMemo(
    () =>
      [
        // Estimated work days, iterate over the work days and create an event for each
        ...taskWorkInterval
          .map((date) => ({
            id: date.toISOString(),
            date,
            allDay: true,
            title: date.getDate().toString(),
            backgroundColor: Palette.Gray300,
          }))
          .filter((event) => !workDays.some((workDay) => workDay.workDay.date.toISOString() === event.id)),
        // Actual work days
        ...workDays.map((workDay) => ({
          id: workDay.id,
          date: workDay.workDay.date,
          allDay: true,
          // The title will be the day of the month
          title: workDay.workDay.date.getDate().toString(),
          backgroundColor: Palette.Blue200,
        })),
        // Add the original start date last so it will be removed in the `uniqueBy` call if it overlaps with the current range
        taskSnapshot?.startDate && {
          id: "originalStartDate",
          date: taskSnapshot.startDate,
          allDay: true,
          title: taskSnapshot.startDate.getDate().toString(),
          backgroundColor: Palette.Yellow200,
        },
      ]
        .compact()
        .uniqueBy((event) => event.date.toISOString()),
    [taskWorkInterval, workDays, taskSnapshot],
  );

  const delayFlags = useMemo(() => task.scheduleFlags, [task.scheduleFlags]);

  const isFlagWithinRange = (createdAtDate: Date, date: Date) => {
    return isEqual(endOfDay(date), endOfDay(createdAtDate));
  };

  // Check if the task is planned for the current month
  const isInCurrentMonth = useMemo(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const calendarDate = calendarApi.getDate();
      return isWithinInterval(calendarDate, {
        start: startOfDay(new DateOnly(task.startDate)),
        end: endOfDay(task.endDate),
      });
    }
    return false;
  }, [calendarRef, task.endDate, task.startDate]);

  // If the task is not in the current month we will render the calendar in the month of the task
  useEffect(() => {
    if (calendarRef.current && !isInCurrentMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(task.startDate);
    }
  }, [calendarRef, isInCurrentMonth, task.startDate]);

  return (
    <div css={Css.gc("1 / span 2").gr(1).df.fdc.bgWhite.br8.hPx(600).$}>
      <div className="hb-dynamic-schedules-calendar-card" css={baseStyles}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin]}
          height={"calc(100% - 30px)"}
          customButtons={{
            // This is a hack to have a header that is not a button
            header: { text: "Task Status", click: () => {} },
            empty: { text: "", click: () => {} },
          }}
          events={events}
          editable={false}
          headerToolbar={{ start: "header", center: "prev title next", end: "empty" }}
          dayHeaderContent={({ text }) => {
            return <div css={Css.lg.gray500.mb1.$}>{text[0]}</div>;
          }}
          dayCellContent={({ dayNumberText, isOther, date }) => {
            const currentDayDelayFlags = delayFlags?.filter((f) => isFlagWithinRange(f.createdAt, date));
            return (
              <div css={Css.relative.df.aic.jcc.w100.h100.$}>
                {currentDayDelayFlags.nonEmpty && (
                  <DateIcons
                    delayFlag={currentDayDelayFlags?.map((d) => d.reason?.title).join(", ")}
                    // TODO: Add the logic for materialDrop and tradePartnerAssigned
                  />
                )}
                <div css={Css.lg.if(isOther).dn.else.gray800.$}>{dayNumberText}</div>
              </div>
            );
          }}
          eventContent={({ event }) => {
            const currentDayDelayFlags = delayFlags?.filter((f) => isFlagWithinRange(f.createdAt, event.start!));
            return (
              <div css={Css.relative.df.aic.jcc.w100.h100.$}>
                {currentDayDelayFlags.nonEmpty && (
                  <DateIcons
                    delayFlag={currentDayDelayFlags?.map((d) => d.reason?.title).join(", ")}
                    // TODO: Add the logic for materialDrop and tradePartnerAssigned
                  />
                )}
                <div css={Css.lg.gray800.$}>{event.title}</div>
              </div>
            );
          }}
        />
        <TaskColorLegend
          title={""}
          items={[
            { color: Palette.Blue200, label: "A day that this task was worked on" },
            { color: Palette.Gray300, label: "Estimated work days / No work done" },
            { color: Palette.Yellow200, label: "Original Start Date" },
          ]}
        />
      </div>
    </div>
  );
}

const baseStyles = Css.br8.h100.$;
